import React, { useEffect, useState } from 'react';

import { Box, Typography } from '@mui/material';
import { TextWithInfoIcon } from '../../../shared/components/textWithInfoIcon';
import InfoIcon from '../../../assets/icons/infoIcon.svg';
import ChronicDiseaseIcon from '../../../assets/icons/chronicDisease.svg';
import BMIIconBordered from '../../../assets/icons/bmiIconBordered.svg';
import WeightLossIcon from '../../../assets/icons/weightLossIcon.svg';
import { NavigationButtons } from '../../../shared/components/navigationButtons';
import { BoxStyled } from '../../patientInformation/style';
import { useAppSelector } from '../../../store/hooks';
import { metaDataSelector } from '../../../store/globalSelectors';
import { ArcContainer } from '../../../shared/components/arcContainer';
import { TypographyH4, TypographyH6 } from '../style';
import { BlueScoreCard } from '../../../shared/components/blueScoreCard';
import { CustomPillToggle } from '../../../shared/components/customPillToggle';
import { CustomPercentRing } from '../../../shared/components/customPercentRing';
import { useDispatch } from 'react-redux';
import { setMustValues } from '../../../store/slices/metaDataSlice';
import { InfoModal } from '../../../shared/components/modal/InfoModal';

export const Must = () => {
  const dispatch = useDispatch();
  const [openImageInfoModal, setOpenImageInfoModal] = useState(false);
  const [openInfoModal, setOpenInfoModal] = useState(false);
  const [checked, setChecked] = useState(false);
  const { patientInformation, alternativeMeasures, malnutritionScreening } =
    useAppSelector(metaDataSelector);
  const { bmi, metric, lossPercentage } = patientInformation;
  const { estBMI_ulna } = alternativeMeasures;
  const { must } = malnutritionScreening;
  const { bmiScore, weightLossScore, acuteIllnessScore } = must;
  const { usualWeight } = metric;

  const bmiToBeDisplayed: number = bmi ? bmi : estBMI_ulna;

  const handleCloseModal = () => {
    openImageInfoModal && setOpenImageInfoModal(false);
    openInfoModal && setOpenInfoModal(false);
  };

  const handleClick = () => {
    setChecked(!checked);
  };

  useEffect(() => {
    // Calculate BMI score
    let bmiScore = 0;
    if (bmiToBeDisplayed < 18.5) {
      bmiScore = 2;
    } else if (bmiToBeDisplayed >= 18.5 && bmiToBeDisplayed <= 20) {
      bmiScore = 1;
    }

    // Calculate weight loss score
    let weightLossScore = 0;
    if (lossPercentage > 10) {
      weightLossScore = 2;
    } else if (lossPercentage >= 5 && lossPercentage <= 10) {
      weightLossScore = 1;
    }

    // Calculate acute illness score
    const acuteIllnessScore = checked ? 2 : 0;

    // Calculate total score
    const totalScore = bmiScore + weightLossScore + acuteIllnessScore;

    // Dispatch only if any of the computed values differ from what’s in the store.
    // (This extra check prevents unnecessary dispatches.)
    if (
      must.bmiScore !== bmiScore ||
      must.weightLossScore !== weightLossScore ||
      must.acuteIllnessScore !== acuteIllnessScore ||
      must.totalScore !== totalScore
    ) {
      dispatch(
        setMustValues({
          ...must,
          bmiScore,
          weightLossScore,
          acuteIllnessScore,
          totalScore
        })
      );
    }
  }, [bmiToBeDisplayed, lossPercentage, checked, dispatch, must]);

  return (
    <>
      <BoxStyled>
        {/* Top Bar (title + info icon) */}
        <TextWithInfoIcon
          title={'MUST'}
          icon={InfoIcon}
          fontSize={'1.625rem'}
          lineHeight={'1.375rem'}
          variant={'h1'}
          handleIconClick={() => setOpenInfoModal(true)}
          testId="#must-index-popup"
        />

        <ArcContainer icon={BMIIconBordered} customizedHeight="auto">
          <BoxStyled sx={{ marginTop: '1.875rem' }}>
            <TypographyH4 className="pt-16">Body Mass Index (BMI)</TypographyH4>
            <Typography
              sx={{
                fontFamily: 'Calibri',
                fontWeight: 700,
                fontSize: '45px',
                color: '#007ACC',
                margin: '0.625rem'
              }}
            >
              <span>
                {bmiToBeDisplayed} kg/m<sup>2</sup>
              </span>
            </Typography>
            <Box
              sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}
            >
              <BlueScoreCard label={'BMI Score'} score={bmiScore} />
            </Box>
          </BoxStyled>
        </ArcContainer>

        <ArcContainer icon={WeightLossIcon} customizedHeight="auto">
          <BoxStyled sx={{ marginTop: '1.875rem' }}>
            <TypographyH4 className="pt-16">
              UNINTENTIONAL WEIGHT LOSS
            </TypographyH4>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                width: '100%', // or a fixed width if needed
                // maxWidth: 500, // example
                mx: 'auto', // center horizontally if desired
                padding: '0.625rem'
                // background: 'grey'
              }}
              className="p-0"
            >
              {/* Left Column */}
              <Box sx={{ textAlign: 'center' }}>
                {/* Smaller subtitle */}
                <TypographyH6 variant="body2">
                  USUAL WEIGHT
                  <br />
                  <span
                    style={{
                      fontWeight: 400,
                      fontSize: '0.75rem',
                      fontFamily: 'Calibri',
                      color: '#002A3A'
                    }}
                  >
                    (in the past 3-6 months)
                  </span>
                </TypographyH6>

                {/* Big Blue Text for the weight */}
                <Typography
                  sx={{
                    fontFamily: 'Calibri',
                    fontWeight: 700,
                    fontSize: '45px',
                    color: '#007ACC',
                    margin: '0.625rem',
                    marginTop: '1.25rem'
                  }}
                >
                  {usualWeight} kg
                </Typography>
              </Box>
              {/* Right Column */}
              <Box sx={{ textAlign: 'center' }}>
                {/* Top label "WEIGHT LOSS" */}
                <TypographyH6 variant="body2" sx={{ mb: 1 }}>
                  WEIGHT LOSS
                </TypographyH6>

                <CustomPercentRing percent={lossPercentage} />
              </Box>
            </Box>
            <Box
              sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}
            >
              <BlueScoreCard
                label={'Weight Loss Score'}
                score={weightLossScore}
              />
            </Box>
          </BoxStyled>
        </ArcContainer>

        <ArcContainer icon={ChronicDiseaseIcon} customizedHeight="auto">
          <BoxStyled sx={{ marginTop: '1.875rem' }}>
            <TypographyH4 className="pt-16">ACUTE ILLNESS</TypographyH4>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                width: '100%', // or a fixed width if needed
                mx: 'auto', // center horizontally if desired
                padding: '0.625rem 0.9375rem',
                margin: '0.625rem 0'
              }}
            >
              <Box sx={{ width: '225px' }}>
                <Typography
                  sx={{
                    color: '#002A3A',
                    fontFamily: 'Calibri',
                    fontSize: '1rem'
                  }}
                >
                  Is the patient acutely ill and there has been or is likely to
                  be no nutritional intake for {'>'}5 days?
                </Typography>
              </Box>

              {/* Right: the custom pill toggle */}
              <CustomPillToggle
                handleClick={handleClick}
                checked={checked}
                truthyValue="YES"
                falsyValue="NO"
              />
            </Box>
            <Box
              sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}
            >
              <BlueScoreCard
                label={'Acute Illness Score'}
                score={acuteIllnessScore}
              />
            </Box>
          </BoxStyled>
        </ArcContainer>
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2, mt: 2 }}>
          <NavigationButtons
            nextRoute={'/malnutrition-screening/must/screening-result'}
            nextDisabled={false}
          />
        </Box>
      </BoxStyled>
      <InfoModal
        open={openInfoModal}
        onClose={handleCloseModal}
        title={'Reference: '}
        content={
          'Malnutrition Advisory Group, a Standing Committee of the British Association of Parenteral and Enteral Nutrition (2003) Malnutrition Universal Screening Tool.'
        }
      />
    </>
  );
};
